import { Toast } from "@/components/ui/toast";
import { authService } from "@/services/AuthService";
import { i18n } from "@/utils/i18n";

/**
 * VoteState —— 评论投票状态机
 *
 * 职责：管理投票计数和用户投票状态的转换逻辑。
 * 纯逻辑类，零 DOM 依赖，可独立单元测试。
 */

/** 投票类型 */
export type VoteType = "like" | "dislike";

export interface Entity {
  likeCount: number;
  dislikeCount: number;
  voteType: VoteType | null;
}

/** 评论投票 up-data 传入的投票数据 */
export type VoteData = {
  commentId: number;
} & Entity;

/**
 * Vote —— 评论投票 UI 组件
 *
 * 职责：监听点击事件、调用投票接口、等待服务端响应后更新 UI。
 * 状态管理委托给 VoteState，本类只负责 DOM 操作和网络请求。
 *
 * 未登录时：弹出登录 Modal，登录成功后以 set 语义执行投票，避免意外取消已有投票。
 */
export class Vote {
  private voting = false;
  private commentId: number;

  private likeBtn: HTMLButtonElement | null;
  private dislikeBtn: HTMLButtonElement | null;
  private likeCountEl: HTMLElement | null;
  private dislikeCountEl: HTMLElement | null;
  /** click 事件处理函数引用，destroy 时用于移除监听 */
  private handleLikeClick = () => this.vote("like");
  private handleDislikeClick = () => this.vote("dislike");

  constructor(element: HTMLElement, data: VoteData) {
    const { commentId, likeCount, dislikeCount, voteType } = data;
    this.commentId = commentId;
    this.likeBtn = element.querySelector('[data-vote="like"]');
    this.dislikeBtn = element.querySelector('[data-vote="dislike"]');
    this.likeCountEl = element.querySelector("[data-like-count]");
    this.dislikeCountEl = element.querySelector("[data-dislike-count]");

    if (this.likeBtn) up.on(this.likeBtn, "click", this.handleLikeClick);
    if (this.dislikeBtn)
      up.on(this.dislikeBtn, "click", this.handleDislikeClick);
    this.render({ likeCount, dislikeCount, voteType });
  }

  private async vote(voteType: VoteType): Promise<void> {
    if (this.voting) return;

    if (!authService.isSignedIn) {
      authService.signin({
        onAccepted: async () => {
          // 登录后用 set 语义，避免意外取消已有投票
          await this.perform(voteType, "set");
        },
        onDismissed: () => {
          // 用户关闭登录框，不做任何操作
        },
      });
      return;
    }

    // 已登录用户正常 toggle
    await this.perform(voteType, "toggle");
  }

  /**
   * 执行核心投票流程
   */
  private async perform(
    voteType: VoteType,
    action: "set" | "toggle",
  ): Promise<void> {
    // 进入 loading 状态
    this.voting = true;
    this.setDisabled(true);
    this.setLoading(true);

    try {
      const res = await fetch(
        `${(window as any).__LANG_PREFIX__ || "/zh"}/web/comments/${this.commentId}/vote`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ voteType, action }),
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      this.render(await res.json());
    } catch (_err) {
      Toast.error(i18n.t("comments.vote_failed"));
    } finally {
      this.voting = false;
      this.setDisabled(false);
      this.setLoading(false);
    }
  }

  private setLoading(loading: boolean): void {
    this.likeBtn?.classList.toggle("opacity-50", loading);
    this.dislikeBtn?.classList.toggle("opacity-50", loading);
  }

  /**
   * 根据当前状态更新 DOM
   * 使用 classList.toggle 替代字符串替换，更安全可靠
   */
  private render(entity: Entity): void {
    this.updateButtonState(
      this.likeBtn,
      this.likeCountEl,
      entity.likeCount,
      "text-primary",
      entity.voteType === "like",
    );
    this.updateButtonState(
      this.dislikeBtn,
      this.dislikeCountEl,
      entity.dislikeCount,
      "text-orange-500",
      entity.voteType === "dislike",
    );
  }

  /**
   * 更新单个按钮及其计数的 UI 状态
   */
  private updateButtonState(
    btn: HTMLButtonElement | null,
    countEl: HTMLElement | null,
    count: number,
    activeClass: string,
    isActive: boolean,
  ): void {
    if (countEl) {
      countEl.textContent = String(count);
    }

    if (!btn) return;

    // 切换按钮颜色和悬停效果
    btn.classList.toggle(activeClass, isActive);
    btn.classList.toggle("text-muted", !isActive);
    btn.classList.toggle(`hover:${activeClass}`, !isActive);

    // 同步图标颜色
    const icon = btn.querySelector("svg");
    icon?.classList.toggle(activeClass, isActive);
  }

  private setDisabled(disabled: boolean): void {
    if (this.likeBtn) this.likeBtn.disabled = disabled;
    if (this.dislikeBtn) this.dislikeBtn.disabled = disabled;
  }

  destroy(): void {
    if (this.likeBtn) up.off(this.likeBtn, "click", this.handleLikeClick);
    if (this.dislikeBtn)
      up.off(this.dislikeBtn, "click", this.handleDislikeClick);
  }

  /** 注册 Unpoly 编译器 */
  static compiler(selector: string): void {
    up.compiler(selector, (element: Element, data: VoteData) => {
      const vote = new Vote(element as HTMLElement, data);
      return () => vote.destroy();
    });
  }
}
